import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useCreateCommunityChallengeMutation } from "../store/api/communityApi";
import { useSportWorkspace } from "../hooks/useSportWorkspace";
import { useAnalytics } from "../hooks/useAnalytics";
import { brandFilledButtonSx } from "../utils/brandAssets";
import { communityChallengePath } from "../utils/community";

const TEMPLATES = {
  mlb: [{ id: "mlb_lineup", label: "MLB lineup" }],
  nba: [{ id: "nba_starting_five", label: "NBA starting five" }],
  nfl: [
    { id: "nfl_two_way", label: "Offense, defense, and special teams" },
    { id: "nfl_offense", label: "Offense only" },
  ],
};

const CommunityChallengeCreate = () => {
  const { sport } = useSportWorkspace();
  const navigate = useNavigate();
  const { trackCustomEvent } = useAnalytics();
  const options = TEMPLATES[sport] || TEMPLATES.mlb;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [rosterTemplate, setRosterTemplate] = useState(options[0].id);
  const [onePerDecade, setOnePerDecade] = useState(false);
  const [error, setError] = useState("");
  const [createChallenge, { isLoading }] = useCreateCommunityChallengeMutation();

  const rules = useMemo(
    () => (onePerDecade ? [{ type: "max_players_per_decade", value: 1 }] : []),
    [onePerDecade]
  );

  const handleCreate = async () => {
    try {
      const challenge = await createChallenge({
        sport,
        title,
        description,
        rosterTemplate,
        rules,
      }).unwrap();
      trackCustomEvent("challenge_created", { sport, challenge_id: challenge.id });
      navigate(communityChallengePath(sport, challenge.slug || challenge.id));
    } catch (err) {
      setError(err?.data?.error || "Could not create challenge");
    }
  };

  return (
    <Box sx={{ py: { xs: 1, sm: 2 }, maxWidth: 640, mx: "auto" }}>
      <Typography variant="overline" color="text.secondary">
        Create challenge
      </Typography>
      <Typography variant="h3" fontWeight={800} sx={{ mt: 0.5, mb: 3 }}>
        Set the rules. Let the community build.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "grid", gap: 2 }}>
        <TextField
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
        <TextField
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          multiline
          minRows={3}
        />
        <FormControl>
          <InputLabel id="template-label">Roster template</InputLabel>
          <Select
            labelId="template-label"
            label="Roster template"
            value={rosterTemplate}
            onChange={(event) => setRosterTemplate(event.target.value)}
          >
            {options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant={onePerDecade ? "contained" : "outlined"}
          onClick={() => setOnePerDecade((value) => !value)}
        >
          {onePerDecade ? "Rule on: one player per decade" : "Add rule: one player per decade"}
        </Button>
        <Button
          variant="contained"
          sx={brandFilledButtonSx}
          disabled={isLoading || title.trim().length < 3}
          onClick={handleCreate}
        >
          Publish challenge
        </Button>
      </Box>
    </Box>
  );
};

export default CommunityChallengeCreate;
